import {
    Breadcrumb,
    IBreadcrumbItem,
    IBreadcrumbStyles,
    IRenderFunction,
    Link,
    mergeStyles,
    Text,
    useTheme
} from "@fluentui/react"
import { RootState } from "app/store"
import { trimGUIDLower } from "global/Utility/TSFunctions"
import { bcbs_groupconfigurationsnapshot } from "model/AttributeOptionSets.type"
import { memo, useCallback, useMemo } from "react"
import { shallowEqual, useSelector, useStore } from 'react-redux'
import { useHistory, useLocation } from "react-router"
import { AppNavigateToExternal, ENavigationReason } from "."

interface IBreadcrumbLabel {
    text: string;
    data: string;
}

const pageLabels = new Map<string, IBreadcrumbLabel>([
    ["nonstandardtemplates", { text: "Non-Standards", data: "Non-Standard Templates" }],
    ["compare", { text: "Compare", data: "Compare" }],
    ["underwriting", { text: "Underwriting", data: "Underwriting" }],
    ["associationtemplates", { text: "Association Template", data: "Association Template" }],
]);

const primeLabels = new Map([
    ["florida", "Florida Prime"],
    ["capital", "Capital Prime"],
]);

const checklistLabel: IBreadcrumbLabel = { text: "E&M CheckList", data: "E&M CheckList" };

const breadcrumbStyles: Partial<IBreadcrumbStyles> = {
    root: { selectors: { '.ms-TooltipHost': { fontSize: 'medium' } } },
};

function selectBreadcrumbData(state: RootState) {
    return {
        snapshotName: state.data.snapshot?.bcbs_name,
        snapshotStatus: state.data.snapshot?.statuscode,
        accountName: state.status.account?.name,
        plans: state.data.json?.plans,
        benefits: state.data.json?.benefits,
        feesAndPremiums: state.data.json?.feesAndPremiums,
        stopLoss: state.data.json?.stopLoss,
        claimsARs: state.data.json?.claimsARs,
        checkList: state.data.checkList,
    };
}

type TBreadcrumbData = ReturnType<typeof selectBreadcrumbData>;

function getSnapshotLabel(data: TBreadcrumbData): IBreadcrumbLabel {
    const statuses = bcbs_groupconfigurationsnapshot.statuscode;
    let resource = "GroupConfiguration";
    if (data.snapshotStatus === statuses["Gold Standard(808900006)"].value)
        resource += ' 🥇';
    else if (
        data.snapshotStatus === statuses["Cancellation in Progress(808900013)"].value
        || data.snapshotStatus === statuses["Cancelled(808900011)"].value
    )
        resource += ' ❌';

    return { text: data.snapshotName ?? "Group Configuration", data: resource };
}

function resolveBreadcrumbLabel(resource: string, segment: string, url: string, data: TBreadcrumbData): IBreadcrumbLabel | undefined {
    const id = trimGUIDLower(segment);
    switch (resource) {
        case "groupconfiguration":
            return getSnapshotLabel(data);
        case "bluerelationship":
            return { text: data.accountName ?? "Blue Relationship", data: "BlueRelationship" };
        case "prime": {
            const primeLabel = primeLabels.get(segment.toLowerCase());
            return primeLabel ? { text: primeLabel, data: "Extract" } : undefined;
        }
        case "plan": {
            const plan = data.plans?.find(plan => plan.id === id);
            const region = url.match(/\/prime\/(florida|capital)\/plan\//i)?.[1]?.toLowerCase();
            return { text: plan?.name ?? "Plan", data: region ? `${primeLabels.get(region)} Extract` : "Plan" };
        }
        case "benefit": {
            const benefit = data.benefits?.find(benefit => benefit.id === id);
            return {
                text: benefit?.name ?? "Benefit",
                data: benefit?.bcbs_name ? `Benefit (${benefit.bcbs_name})` : "Benefit",
            };
        }
        case "feesandpremium":
            return { text: data.feesAndPremiums?.find(fees => fees.id === id)?.name ?? "Fees and Premium", data: "FeesAndPremium" };
        case "stoploss":
            return { text: data.stopLoss?.find(stopLoss => stopLoss.id === id)?.name ?? "Stop Loss", data: "StopLoss" };
        case "claimsar":
            return { text: data.claimsARs?.find(claimsAR => claimsAR.id === id)?.name ?? "Accounts Receivable", data: "Accounts Receivable" };
        case "checklist": {
            if (segment.toLowerCase() === "list")
                return checklistLabel;
            const checklist = data.checkList?.find(checklist => checklist.bcbs_emchecklist.bcbs_emchecklistid === id);
            return { text: checklist?.bcbs_emchecklist.bcbs_name ?? checklistLabel.text, data: "Plan" };
        }
        case "pharmacy":
            if (/\/groupconfiguration\/[^/]+\/pharmacy\//i.test(url))
                return { text: data.plans?.find(plan => plan.id === id)?.name ?? "Plan", data: "Prime Extract" };
            return undefined;
        default:
            if (/\/groupconfiguration\/[^/]+\/pharmacy$/i.test(url))
                return { text: "Pharmacy", data: "Extract" };
            return pageLabels.get(segment.toLowerCase());
    }
}

function buildBreadcrumbItems(
    pathname: string,
    data: TBreadcrumbData,
    navigate: (url: string) => void,
    openAccount: () => void,
): IBreadcrumbItem[] {
    const segments = pathname.split('/').filter(Boolean);
    const items: IBreadcrumbItem[] = [];
    let url = "";

    segments.forEach((segment, index) => {
        const parentUrl = url;
        url += `/${segment}`;
        const resource = segments[index - 1]?.toLowerCase() ?? "";
        const label = resolveBreadcrumbLabel(resource, segment, url, data);
        if (!label)
            return;

        if (resource === "checklist" && segment.toLowerCase() !== "list") {
            const listUrl = `${parentUrl}/list`;
            items.push({ ...checklistLabel, key: listUrl, onClick: () => navigate(listUrl) });
        }

        const itemUrl = url;
        items.push({
            ...label,
            key: itemUrl,
            onClick: resource === "bluerelationship" ? openAccount : () => navigate(itemUrl),
        });
    });

    return items;
}

const BreadcrumbNavigation = () => {

    const history = useHistory();
    const { pathname } = useLocation();
    const store = useStore<RootState>();
    const data = useSelector(selectBreadcrumbData, shallowEqual);
    const theme = useTheme();

    const items = useMemo(() => buildBreadcrumbItems(
        pathname,
        data,
        url => history.push(url),
        () => AppNavigateToExternal(history, store.getState(), ENavigationReason.CRM_ACCOUNT),
    ), [pathname, data, history, store]);

    const secondaryClass = useMemo(() => {
        return mergeStyles({
            padding: '3px',
            paddingLeft: '6px',
            paddingRight: '6px',
            border: '1px solid transparent',
            margin: '2px',
            boxSizing: 'border-box',
            borderRadius: '4px',
            "&:hover": {
                textDecoration: 'none',
                backgroundColor: theme.palette.white,
                border: '1px solid gray',
                transition: 'all 0.4s',

            },
            "&:active:hover": {
                textDecoration: 'none',
            }

        });
    }, [theme.palette.white]);

    const renderItem = useCallback<IRenderFunction<IBreadcrumbItem>>(item => item ? (
        <Link onClick={item.onClick} className={secondaryClass}>
            <div><Text variant="medium">{item.text}</Text></div>
            <div><Text variant="xSmall">{item.data}</Text></div>
        </Link>
    ) : null, [secondaryClass]);

    return (
        <div>
            <Breadcrumb
                items={items}
                onRenderItem={renderItem}
                maxDisplayedItems={10}
                styles={breadcrumbStyles}
            />
        </div>
    )
}

export default memo(BreadcrumbNavigation);